import {
  ButtonDS,
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
  NumericInput,
  SelectDS,
  Spacer,
  TableDS,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterLongMonthYear,
  triggerToast,
  unreachable,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  invoiceSlice,
  createInvoiceAsync,
  getInvoicesListAsync,
  invoiceDownloadAsync,
  invoiceExportAsync,
  selectCreateInvoiceStatus,
  selectInvoicesList,
  selectInvoiceExportLink,
  getInvoiceCompaniesSettingsAsync,
  selectInvoiceCompaniesSettings,
  selectUpdateInvoiceCompaniesSettingsStatus,
  updateInvoiceCompaniesSettingsAsync,
} from "./invoiceSlice";
import { InvoicesListDisplayed, InvoicesListDisplayedRow } from "./invoiceApi";
import {
  companiesListAsync,
  selectCompaniesList,
  selectCompaniesListStatus,
} from "./../companiesSlice";
import { useNavigate, useParams } from "react-router-dom";
import "../../i18n/i18n";
import { selectAdminUser } from "../homeSlice";

const monthValueList = (
  creationDate: string,
  invoices: InvoicesListDisplayed[],
) => {
  const optionMonth: { value: string; label: string }[] = [];
  const now = new Date(new Date().setMonth(new Date().getMonth() - 1));
  const beginningDate = new Date(creationDate);
  beginningDate.setDate(1);

  for (
    let i = beginningDate;
    i < now;
    i = new Date(beginningDate.setMonth(beginningDate.getMonth() + 1))
  ) {
    const currentMonth = invoices.find((invoice) => {
      return (
        new Date(invoice.date).getMonth() === i.getMonth() &&
        new Date(invoice.date).getFullYear() === i.getFullYear()
      );
    });
    if (!currentMonth) {
      optionMonth.push({
        value: `${new Date(i).toISOString().substring(0, 10)}`,
        label: `${dateFormatterLongMonthYear(new Date(i))}`,
      });
    }
  }
  return optionMonth;
};

export const InvoiceTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const adminUser = useAppSelector(selectAdminUser);
  const companiesList = useAppSelector(selectCompaniesList);
  const companiesListStatus = useAppSelector(selectCompaniesListStatus);
  const createInvoiceStatus = useAppSelector(selectCreateInvoiceStatus);
  const updateInvoiceCompaniesSettingsStatus = useAppSelector(
    selectUpdateInvoiceCompaniesSettingsStatus,
  );
  const invoicesList = useAppSelector(selectInvoicesList);
  const invoiceCompaniesSettings = useAppSelector(
    selectInvoiceCompaniesSettings,
  );
  const invoiceExportLink = useAppSelector(selectInvoiceExportLink);
  const [companySelected, setCompanySelected] = useState<string>("");
  const [companyCreation, setCompanyCreation] = useState<string>("");
  const [monthSelected, setMonthSelected] = useState<string>("");
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [daysUntilDueToUpdate, setDaysUntilDueToUpdate] = useState<number | "">(
    "",
  );
  const [isDaysUntilDueUpdated, setIsDaysUntilDueUpdated] =
    useState<boolean>(false);

  const isValidSubmission = useMemo(
    () => companySelected !== "" && monthSelected !== "",
    [companySelected, monthSelected],
  );

  const optionMonth = useMemo(
    () => monthValueList(companyCreation, invoicesList),
    [companyCreation, invoicesList],
  );

  const valueOptionMonth = useMemo(
    () => optionMonth.find((option) => option.value === monthSelected),
    [optionMonth, monthSelected],
  );
  const optionCompany = useMemo(
    () =>
      companiesList.map((company) => {
        return {
          value: company.uuid,
          label: company.name,
        };
      }),
    [companiesList],
  );

  const valueOptionCompany = useMemo(
    () => optionCompany.find((option) => option.value === companySelected),
    [optionCompany, companySelected],
  );

  const resetValue = useCallback(() => {
    setCompanySelected("");
    setMonthSelected("");
    setDaysUntilDueToUpdate("");
    setIsDaysUntilDueUpdated(false);
  }, []);

  useEffect(() => {
    if (companiesListStatus === "idle") {
      void dispatch(companiesListAsync());
    }
  }, [dispatch, companiesListStatus]);

  useEffect(() => {
    if (params.id && !companySelected) {
      setCompanySelected(params.id);
    }
  }, [companySelected, params.id]);

  useEffect(() => {
    if (companySelected) {
      void dispatch(getInvoicesListAsync({ company: companySelected }));
      void dispatch(
        getInvoiceCompaniesSettingsAsync({ company: companySelected }),
      );
      navigate(`/home/billings/${params.tab}/${companySelected}`);
    }
  }, [companySelected, dispatch, createInvoiceStatus, navigate, params.tab]);

  useEffect(() => {
    if (createInvoiceStatus === "success") {
      triggerToast(t("invoice.creation.success") || "", "valid");
      void dispatch(getInvoicesListAsync({ company: companySelected }));
      dispatch(invoiceSlice.actions.resetCreateInvoiceStatus());
    } else if (createInvoiceStatus === "failed") {
      triggerToast(t("invoice.creation.failure") || "", "error");
      dispatch(invoiceSlice.actions.resetCreateInvoiceStatus());
    }
  }, [createInvoiceStatus, t, dispatch, companySelected]);

  useEffect(() => {
    if (updateInvoiceCompaniesSettingsStatus === "success") {
      triggerToast(
        t("invoice.companySettings.daysUntilDue.success") || "",
        "valid",
      );
      void dispatch(
        getInvoiceCompaniesSettingsAsync({ company: companySelected }),
      );
    } else if (updateInvoiceCompaniesSettingsStatus === "failed") {
      triggerToast(
        t("invoice.companySettings.daysUntilDue.failure") || "",
        "error",
      );
    }
    dispatch(invoiceSlice.actions.resetUpdateInvoiceCompaniesSettingsStatus());
  }, [updateInvoiceCompaniesSettingsStatus, t, dispatch, companySelected]);

  useEffect(() => {
    if (invoiceExportLink.presignedUrl && invoiceExportLink.fileName) {
      void dispatch(invoiceDownloadAsync(invoiceExportLink));
    }
  }, [invoiceExportLink, dispatch]);

  const selectCompany = useCallback(
    (companyUuid: string) => {
      setCompanySelected(companyUuid);
      const companyFound = companiesList.find(
        (row) => row.uuid === companyUuid,
      );
      if (companyFound) {
        setCompanyCreation(companyFound.createdDate);
      }
    },
    [companiesList],
  );

  useEffect(() => {
    if (companySelected && !companyCreation) {
      const companyFound = companiesList.find(
        (row) => row.uuid === companySelected,
      );
      if (companyFound) {
        setCompanyCreation(companyFound.createdDate);
      }
    }
  }, [
    companiesList,
    companiesList.length,
    companyCreation,
    companySelected,
    params.id,
    selectCompany,
  ]);

  const clickToSubmit = useCallback(() => {
    if (!isValidSubmission) {
      setDisplayError(true);
    } else {
      setDisplayError(false);
      void dispatch(
        createInvoiceAsync({
          company: companySelected,
          date: new Date(monthSelected).toISOString(),
        }),
      );
      resetValue();
    }
  }, [companySelected, dispatch, isValidSubmission, monthSelected, resetValue]);

  const submitUpdateDaysUntilDue = useCallback(() => {
    if (!adminUser || !daysUntilDueToUpdate || daysUntilDueToUpdate === 0) {
      return;
    }
    void dispatch(
      updateInvoiceCompaniesSettingsAsync({
        company: companySelected,
        daysUntilDue: daysUntilDueToUpdate,
        updatedBy: adminUser.email,
      }),
    );
    setDaysUntilDueToUpdate("");
  }, [dispatch, companySelected, daysUntilDueToUpdate, adminUser]);

  if (params.tab !== "invoices") {
    return;
  }

  const headers = {
    company: {
      text: t("invoice.column.company"),
    },
    date: {
      text: t("invoice.column.date"),
    },
    createdAt: {
      text: t("invoice.column.createdAt"),
    },
    dueDate: {
      text: t("invoice.column.dueDate"),
    },
    bucketFileName: {
      text: t("invoice.column.download"),
    },
    activeTransactions: {
      text: t("invoice.column.activeTransactions"),
    },
    activeCards: {
      text: t("invoice.column.activeCards"),
    },
    badgeTransactions: {
      text: t("invoice.column.badgeTransactions"),
    },
    activeBadges: {
      text: t("invoice.column.activeBadges"),
    },
    analyticVehicles: {
      text: t("invoice.column.analyticVehicles"),
    },
    advancedAnalyticVehicles: {
      text: t("invoice.column.advancedAnalyticVehicles"),
    },
  };

  const render =
    (row: InvoicesListDisplayedRow) => (key: keyof InvoicesListDisplayed) => {
      switch (key) {
        case "bucketFileName":
          return row[key] ? (
            <ButtonDS
              sizeButton={"S"}
              format={"hug"}
              buttonType={"secondary"}
              singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
              onClick={() => {
                void dispatch(
                  invoiceExportAsync({
                    bucketFileName: row[key] ?? "",
                    fileName: row["fileName"] ?? "",
                  }),
                );
              }}
            />
          ) : (
            ""
          );
        case "company":
          return valueOptionCompany?.label;
        case "createdAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(row[key]));
        case "dueDate":
          return (
            row[key] && dateFormatterDayMonthYearLong2Digits(new Date(row[key]))
          );
        case "date":
          return dateFormatterLongMonthYear(new Date(row[key]));
        case "analyticVehicles":
        case "advancedAnalyticVehicles":
          return row[key] + " vehicles";
        case "activeCards":
          return row[key] + " cartes";
        case "activeBadges":
          return row[key] + " badges";
        case "activeTransactions":
        case "badgeTransactions":
          return row[key] + " transactions";
      }
      unreachable(key);
    };

  return (
    <StyledContainer>
      <Spacer y={1} />
      <StyledRow>
        <StyledLeftColumn>
          <TextCapitalized>{t("invoice.select.company")}</TextCapitalized>
          <SelectDS
            label={""}
            value={valueOptionCompany}
            options={optionCompany}
            onChange={(selectedOption) =>
              selectCompany(selectedOption ? selectedOption.value : "")
            }
            error={
              (displayError &&
                companySelected === "" &&
                t("invoice.select.company.error")) ||
              undefined
            }
            allWidth
          />
          <TextCapitalized>{t("invoice.select.month")}</TextCapitalized>
          <SelectDS
            label={""}
            value={valueOptionMonth}
            options={optionMonth}
            onChange={(selectedOption) =>
              setMonthSelected(selectedOption ? selectedOption.value : "")
            }
            error={
              (displayError &&
                companySelected === "" &&
                t("invoice.select.month.error")) ||
              undefined
            }
            allWidth
          />
          <ButtonDS
            format="hug"
            buttonType="primary"
            text={t("invoice.button.create")}
            onClick={clickToSubmit}
            disabled={!isValidSubmission}
          />
        </StyledLeftColumn>
        <Spacer x={10} />
        <StyledColumn>
          <Spacer y={2} />
          {invoiceCompaniesSettings && (
            <>
              <StyledColumn>
                <Spacer y={0.5} />
                <TextCapitalized>
                  {t("invoice.companySettings.daysUntilDue")}
                </TextCapitalized>
                <StyledRowCenter>
                  <StyledColumn>
                    {isDaysUntilDueUpdated ? (
                      <>
                        <Spacer y={1.5} />
                        <NumericInput
                          value={daysUntilDueToUpdate}
                          $update={(value) => setDaysUntilDueToUpdate(value)}
                          width={10}
                          error={
                            daysUntilDueToUpdate === 0
                              ? t("invoice.companySettings.daysUntilDue.error")
                              : undefined
                          }
                        />
                      </>
                    ) : (
                      t("invoice.companySettings.daysUntilDue.value", {
                        days: invoiceCompaniesSettings.daysUntilDue,
                      })
                    )}
                  </StyledColumn>
                  <Spacer x={1.5} />
                  <ButtonDS
                    buttonType={"primary"}
                    format={"hug"}
                    singleIcon={{
                      icon:
                        isDaysUntilDueUpdated && daysUntilDueToUpdate !== "" ? (
                          <CheckOutlined />
                        ) : (
                          <EditOutlined />
                        ),
                      size: "S",
                    }}
                    onClick={() => {
                      if (!isDaysUntilDueUpdated) {
                        setIsDaysUntilDueUpdated(true);
                      } else if (daysUntilDueToUpdate !== "") {
                        submitUpdateDaysUntilDue();
                        setIsDaysUntilDueUpdated(false);
                      }
                    }}
                  />
                </StyledRowCenter>
              </StyledColumn>
            </>
          )}
        </StyledColumn>
      </StyledRow>
      <Spacer y={2} />
      <StyledTable>
        <TableDS<keyof InvoicesListDisplayed, InvoicesListDisplayed>
          data={invoicesList}
          headers={headers}
          render={render}
        />
      </StyledTable>
      <Spacer y={3} />
    </StyledContainer>
  );
};

const StyledRow = styled.div`
  display: flex;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTable = styled.div`
  height: 100%;
  overflow: auto;
`;

const StyledLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 30rem;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRowCenter = styled.div`
  display: flex;
  align-items: center;
  height: 5rem;
`;
